import { Container, Graphics, Assets, Sprite } from 'pixi.js';
import { TILE_VOID, TILE_FLOOR, TILE_WALL, TILE_DOOR, TILE_CORRIDOR_FLOOR, TILE_STAIRS } from './DungeonGenerator.js';
import { TILE_SIZE } from '../main.js';

// Tile colors
const TILE_COLORS = {
  [TILE_VOID]: 0x000000,
  [TILE_FLOOR]: 0x2a2a3a,
  [TILE_WALL]: 0x1a1a2e,
  [TILE_DOOR]: 0x3a3a2a,
  [TILE_CORRIDOR_FLOOR]: 0x252535,
  [TILE_STAIRS]: 0x44cc44,
};

// Map object sprite paths (relative to public/)
const MAP_OBJECT_ASSETS = {
  chest: 'assets/objects/chest.png',
  torch: 'assets/objects/torch.png',
  shrine: 'assets/objects/shrine.png',
  barrel: 'assets/objects/barrel.png',
  pillar: 'assets/objects/pillar.png',
  merchant_cart: 'assets/objects/merchant_cart.png',
  stairs: 'assets/objects/stairs.png',
  door: 'assets/objects/door.png',
};

// Map object display sizes (in tiles)
const MAP_OBJECT_SIZES = {
  chest: 1,
  torch: 1,
  shrine: 1.5,
  barrel: 1,
  pillar: 1,
  merchant_cart: 1.5,
  stairs: 1,
  door: 1,
};

/**
 * Renders a dungeon tile grid into a PixiJS Container.
 * Batches tiles by color into separate Graphics objects for performance.
 * Also renders map objects as sprites.
 */
export class DungeonRenderer {
  constructor() {
    this.container = null;
    this._texturesLoaded = false;
    this._textures = {};
  }

  /**
   * Preload all map object textures. Call once before rendering.
   * @returns {Promise<void>}
   */
  async loadAssets() {
    if (this._texturesLoaded) return;

    const entries = Object.entries(MAP_OBJECT_ASSETS);
    for (const [key, path] of entries) {
      try {
        this._textures[key] = await Assets.load(path);
      } catch (e) {
        // Texture not found -- will fall back to colored rectangle
        this._textures[key] = null;
      }
    }
    this._texturesLoaded = true;
  }

  /**
   * Render dungeon data into a Container.
   * @param {{ tiles: number[][], mapObjects?: Array<{type: string, x: number, y: number}> }} dungeonData
   * @returns {Container} Container with all tiles rendered
   */
  render(dungeonData) {
    const { tiles, mapObjects } = dungeonData;
    const container = new Container();
    const height = tiles.length;
    const width = tiles[0].length;

    // Group tile positions by type for batched rendering
    const tileGroups = {};
    for (const type of [TILE_VOID, TILE_FLOOR, TILE_WALL, TILE_DOOR, TILE_CORRIDOR_FLOOR, TILE_STAIRS]) {
      tileGroups[type] = [];
    }

    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        const tileType = tiles[row][col];
        if (tileGroups[tileType]) {
          tileGroups[tileType].push({ col, row });
        }
      }
    }

    // Create one Graphics object per tile type (batch by color)
    for (const [type, positions] of Object.entries(tileGroups)) {
      if (positions.length === 0) continue;

      // Skip void tiles -- they just show the background color
      const typeNum = parseInt(type);
      if (typeNum === TILE_VOID) continue;

      const color = TILE_COLORS[typeNum];
      const gfx = new Graphics();

      for (const { col, row } of positions) {
        gfx.rect(col * TILE_SIZE, row * TILE_SIZE, TILE_SIZE, TILE_SIZE);
      }

      gfx.fill(color);
      container.addChild(gfx);
    }

    // Render map objects as sprites (or colored fallback rectangles)
    if (mapObjects && mapObjects.length > 0) {
      this._renderMapObjects(container, mapObjects);
    }

    this.container = container;
    return container;
  }

  /**
   * Render map objects into the dungeon container.
   */
  _renderMapObjects(container, mapObjects) {
    for (const obj of mapObjects) {
      const texture = this._textures[obj.type];
      const sizeTiles = MAP_OBJECT_SIZES[obj.type] || 1;
      const displaySize = sizeTiles * TILE_SIZE;

      if (texture) {
        const sprite = new Sprite(texture);
        // Scale 400px source to desired tile size
        sprite.width = displaySize;
        sprite.height = displaySize;
        // Center on tile position
        sprite.x = obj.x * TILE_SIZE + (TILE_SIZE - displaySize) / 2;
        sprite.y = obj.y * TILE_SIZE + (TILE_SIZE - displaySize) / 2;
        container.addChild(sprite);
      } else {
        // Fallback: colored rectangle
        const gfx = new Graphics();
        const colors = {
          chest: 0xcc8833,
          torch: 0xff6600,
          shrine: 0x44ddcc,
          barrel: 0x8b6914,
          pillar: 0x888888,
          merchant_cart: 0x44aa88,
          stairs: 0x44cc44,
          door: 0x3a3a2a,
        };
        const color = colors[obj.type] || 0xffffff;
        const size = Math.round(displaySize * 0.8);
        const offset = (TILE_SIZE - size) / 2;
        gfx.rect(obj.x * TILE_SIZE + offset, obj.y * TILE_SIZE + offset, size, size);
        gfx.fill(color);
        container.addChild(gfx);
      }
    }
  }
}
